import type { CommandCenterDataSourceRow, DataSourceStatus } from "../lib/commandCenterViewModel";

interface CommandCenterDataWiringPanelProps {
  rows: CommandCenterDataSourceRow[];
}

const S = {
  wrap: {
    background: "rgba(12,12,24,0.85)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: "8px",
    padding: "10px",
    marginBottom: "12px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    paddingBottom: "6px",
    marginBottom: "6px",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  },
  title: {
    fontSize: "10px",
    fontWeight: 800,
    letterSpacing: "0.12em",
    color: "#a0a0c0",
    textTransform: "uppercase" as const,
  },
  meta: {
    marginLeft: "auto",
    fontSize: "9px",
    color: "#707090",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1.25fr 0.75fr 0.85fr 1.1fr 1.25fr 70px 60px",
    alignItems: "center",
    gap: "6px",
    padding: "4px 0",
    borderBottom: "1px solid rgba(255,255,255,0.03)",
  },
  headCell: {
    fontSize: "8px",
    fontWeight: 700,
    letterSpacing: "0.1em",
    color: "#707090",
    textTransform: "uppercase" as const,
  },
  cell: {
    fontSize: "9px",
    color: "#c0c0d0",
    minWidth: 0,
  },
  note: {
    marginTop: "6px",
    fontSize: "9px",
    color: "#707090",
  },
};

function statusColor(status: DataSourceStatus): string {
  if (status === "live") return "#22c55e";
  if (status === "loading") return "#f59e0b";
  if (status === "disabled") return "#64748b";
  return "#ef4444";
}

export function CommandCenterDataWiringPanel({ rows }: CommandCenterDataWiringPanelProps) {
  return (
    <section style={S.wrap} aria-label="Command Center data wiring">
      <div style={S.header}>
        <span style={S.title}>Command Center Data Wiring</span>
        <span style={S.meta}>source · freshness · fallback · behavior</span>
      </div>
      <div role="table" aria-label="Command Center data source contracts">
        <div style={S.row} role="row">
          <span style={S.headCell}>Panel</span>
          <span style={S.headCell}>Source</span>
          <span style={S.headCell}>Transport</span>
          <span style={S.headCell}>Freshness</span>
          <span style={S.headCell}>Fallback</span>
          <span style={S.headCell}>Decision impact</span>
          <span style={S.headCell}>Behavior</span>
          <span style={S.headCell}>Status</span>
        </div>
        {rows.map((row) => (
          <div style={S.row} role="row" key={row.panel}>
            <span style={{ ...S.cell, fontWeight: 800, color: "#e0e0f0" }}>{row.panel}</span>
            <span style={S.cell}>{row.source}</span>
            <span style={S.cell}>{row.transport}</span>
            <span style={S.cell}>{row.freshness}</span>
            <span style={S.cell}>{row.fallback}</span>
            <span style={S.cell}>{row.decisionImpact}</span>
            <span style={S.cell}>{row.behavior}</span>
            <span style={{ ...S.cell, color: statusColor(row.status), fontWeight: 800 }}>{row.status}</span>
          </div>
        ))}
      </div>
      <div style={S.note}>Audit surface only. This panel documents wiring and does not change trading logic, alerts, gates, thresholds, routing, or execution.</div>
    </section>
  );
}
